import React from "react";
import CardPage from "~/components/CardPage";
import { navigate } from "gatsby";
import { Formik, Form } from "formik";
import * as yup from "yup";
import CleverButton from "~/components/CleverButton";
import GoogleSsoButton from "~/components/GoogleSsoButton";
import useFormikStatusSubmit from "~/hooks/useFormikStatusSubmit";
import { useApiClient } from "~/hooks/apiContext";
import { useAuthState } from "~/hooks/authContext";
import { SchemaFieldValues } from "~/utils/SchemaFieldValues";
import Seo from "~/components/Seo";
import authBackgroundImage from "~/images/auth-background.jpg";
import "./Login.css";
import TextFormItem from "~/components/forms/TextFormItem";
import FormStatusError from "~/components/forms/FormStatusError";
import SubmitButton from "~/components/forms/SubmitButton";

const validationSchema = yup
  .object({
    username: yup.string().required(),
    password: yup.string().required(),
  })
  .required();

type FormValues = SchemaFieldValues<typeof validationSchema>;

const initialValues: FormValues = {
  username: "",
  password: "",
};

type LoginProps = {
  onSuccess?: () => void;
  hideSsoButtons?: boolean;
  onClose?: () => void;
};

function Login({ onSuccess, hideSsoButtons = false, onClose }: LoginProps) {
  const apiClient = useApiClient();
  const authState = useAuthState();

  const onSubmit = useFormikStatusSubmit(
    async ({ username, password }: FormValues) => {
      const response = await apiClient.login(username, password);
      if (authState.type !== "signedOut") {
        throw new Error("Invalid state - should be signed out");
      }
      console.log("Login response:", response);

      authState.signIn({
        id: response.id, //
        username: response.username, //
        emailAddress: response.emailAddress, //
        loginType: response.loginType, //
        type: response.userType,
        authToken: response.authToken,
        // ADD SUBSCRIPTION FIELDS
        isSubscribed: response.bIsSubscribed,
        currentAccess: response.aCurrentAccess,
      });

      if (
        typeof window !== "undefined" &&
        window.location.search.includes("fromCart=true")
      ) {
        document.dispatchEvent(new Event("toggle-cart"));
        return; // ⛔ stop navigation out of checkout context
      }

      if (onSuccess) {
        onSuccess(); // ✅ Handles modal close when login from cart modal is used
      } else {
        await navigate("/dashboard/"); // ✅ Normal login navigation
      }
    },
  );

  return (
    <CardPage backgroundImage={authBackgroundImage}>
      <Formik
        onSubmit={onSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        <Form className="login-form">
          <h2 className="text-center mb-2">
            Sign In for
            <br />
            RoundEd Learning!
          </h2>
          <div className="mb-2">
            <TextFormItem name="username" label="Username" autoFocus />
            <TextFormItem type="password" name="password" label="Password" />
          </div>
          <FormStatusError />
          <SubmitButton label="Sign In" />

          {/* ✅ Only show these when hideSsoButtons is false */}
          {!hideSsoButtons && (
            <>
              <div style={{ textAlign: "center", marginTop: 15 }}>
                Please use the buttons below to login if an account has been
                created at school with Clever or Google.
              </div>
              <div className="sso-button-container">
                <CleverButton variant="signin" />
                <GoogleSsoButton />
              </div>
            </>
          )}

          <div className="below-form">
            <div className="line-item">
              Forgot Password?
              <button
                type="button"
                className="link-button"
                onClick={(e) => {
                  e.preventDefault(); // ✅ stops form submission
                  if (onClose) onClose();
                  navigate("/forgot-password/?fromCart=true");
                }}
              >
                Reset
              </button>
            </div>
            <div className="line-item">
              Don&apos;t have an account?{" "}
              <button
                type="button"
                className="link-button"
                onClick={(e) => {
                  e.preventDefault(); // ✅ stops form submission
                  if (onClose) onClose();
                  navigate("/signup/");
                }}
              >
                Signup
              </button>
            </div>
          </div>
        </Form>
      </Formik>
    </CardPage>
  );
}

export function Head() {
  return <Seo title="Login" />;
}

export default Login;
